// Live-locations map — the Google map filling the card's map cell. One avatar
// pin per user with a known position (online = success ring; offline = muted
// puck + "Offline" tag) at their LATEST location only — no trails here (user
// decision 2026-11-04; routes live in the Route history drawer).
// Selection is shared with the list (row click pans here, pin click selects
// there); the selected pin grows a card: name + close, a Status / Last seen
// panel, and two actions — "Show route history" opens the person's
// RouteHistoryDrawer (via onShowRoute), while "Pull latest position" (per
// person) is COMING SOON and toasts as much on click (the user's chosen
// pattern, an explicit exception to the no-inert-controls rule).
// Auto-fits the bounds whenever the set of users changes — never on
// selection, so panning to a person doesn't snap back — and again on demand
// from the card's "Show all". The user's own node areas draw underneath as a
// backdrop: a brand dot with the node's name under it, plus a translucent
// circle when the node carries a radius; they never move the camera by
// themselves — only the card's "All areas" dropdown moves it: to one area,
// or (its reset row) around all of them (focus props). Default Google
// rendering by user choice.

import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  CircleF,
  GoogleMap,
  MarkerF,
  OverlayViewF,
  OverlayView,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Avatar, Button, Icon, IconButton } from '@/components/common';
import { ClockInState } from '@/api/endpoints/attendanceApi';
import type { NodeArea, PositionedLiveUser } from '@/api/endpoints/liveTrackingApi';
import { MAP_API_KEY } from '@/config/config';
import toneOf from '@/utils/avatarTone';
import i18n from '@/i18n/config';
import { cssColor } from './utils/cssColor';
import { fmtLastSeen } from './utils/lastSeen';
import initialsOf from '@/utils/initials';

const SINGLE_PIN_ZOOM = 13;
const FIT_PADDING = 56;

/** The overlay wrapper is zero-size and the CSS anchors the puck's pointer
 *  tip on it (selected pins grow a card, so measured-size offsets would
 *  shift the pin) — the overlay itself sits exactly on the coordinate. */
const pinOffset = () => ({ x: 0, y: 0 });

interface LiveLocationsMapProps {
  users: PositionedLiveUser[];
  /** The user's own node areas — backdrop only, never part of the fit. */
  areas: NodeArea[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Bumped by the card's "Show all" — every change re-fits around all pins. */
  fitSignal?: number;
  /** The area picked in the card's "All areas" dropdown; null = all of them. */
  focusAreaId: string | null;
  /** Bumped on every pick — each change centers on `focusAreaId`, or fits
   *  around every area when it is null. */
  focusSignal?: number;
  /** The pin card's "Show route history" — opens that person's route drawer. */
  onShowRoute: (companyUserId: string) => void;
}

const LiveLocationsMap = ({
  users,
  areas,
  selectedId,
  onSelect,
  fitSignal = 0,
  focusAreaId,
  focusSignal = 0,
  onShowRoute,
}: LiveLocationsMapProps) => {
  const { t } = useTranslation();
  const mapRef = useRef<google.maps.Map | null>(null);
  // Google paints with literal colours — read the tokens once per mount.
  const colors = useMemo(() => ({ area: cssColor('--brand'), ring: cssColor('--surface') }), []);

  // The script loads once per session — the language it was first requested
  // with sticks until a full page reload, which is acceptable for map labels.
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAP_API_KEY,
    language: i18n.language,
  });

  // Refit only when who-is-on-the-map changes, not when selection pans it.
  const usersKey = useMemo(() => users.map((u) => u.companyUserId).join(','), [users]);
  const fitToUsers = () => {
    const map = mapRef.current;
    if (!map || users.length === 0) return;
    if (users.length === 1) {
      const { latitude, longitude } = users[0].lastLocation;
      map.panTo({ lat: latitude, lng: longitude });
      map.setZoom(SINGLE_PIN_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    users.forEach((u) =>
      bounds.extend({ lat: u.lastLocation.latitude, lng: u.lastLocation.longitude }),
    );
    map.fitBounds(bounds, FIT_PADDING);
  };
  useEffect(() => {
    fitToUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersKey, isLoaded]);

  // "Show all" from the card — the initial 0 is not a request.
  useEffect(() => {
    if (fitSignal) fitToUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSignal]);

  // Dropdown pick — center on that area, or fit around every area's centre
  // for the reset row (circles are not fitted, by user choice); the initial
  // 0 is not a request.
  useEffect(() => {
    const map = mapRef.current;
    if (!focusSignal || !map) return;
    const targets = focusAreaId ? areas.filter((a) => a.id === focusAreaId) : areas;
    if (targets.length === 0) return;
    if (targets.length === 1) {
      const { latitude, longitude } = targets[0].center;
      map.panTo({ lat: latitude, lng: longitude });
      const zoom = map.getZoom();
      if (zoom !== undefined && zoom < SINGLE_PIN_ZOOM) map.setZoom(SINGLE_PIN_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    targets.forEach((a) => bounds.extend({ lat: a.center.latitude, lng: a.center.longitude }));
    map.fitBounds(bounds, FIT_PADDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal]);

  // Row click in the panel — bring that person into view without re-fitting.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const user = users.find((u) => u.companyUserId === selectedId);
    if (!user) return;
    map.panTo({ lat: user.lastLocation.latitude, lng: user.lastLocation.longitude });
    const zoom = map.getZoom();
    if (zoom !== undefined && zoom < 12) map.setZoom(12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  if (loadError) {
    return (
      <div className='attendance-live__map-state'>
        <Icon name='map-pin' size={28} aria-hidden='true' />
        {t('attendance_live_map_error')}
      </div>
    );
  }
  if (!isLoaded) {
    return <div className='attendance-live__map-state' aria-hidden='true' />;
  }

  /** The pin card's status value — on the clock, paused, or offline. */
  const statusOf = (u: PositionedLiveUser) => {
    if (!u.online) return { key: 'attendance_live_offline', modifier: 'offline' };
    if (u.state === ClockInState.Paused)
      return { key: 'attendance_status_paused', modifier: 'paused' };
    return { key: 'attendance_pin_checked_in', modifier: null };
  };

  const comingSoon = () => {
    toast(t('attendance_coming_soon'));
  };

  return (
    <GoogleMap
      mapContainerClassName='attendance-live__map-canvas'
      onLoad={(map) => {
        mapRef.current = map;
        fitToUsers();
      }}
      onUnmount={() => {
        mapRef.current = null;
      }}
      onClick={() => onSelect(null)}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        clickableIcons: false,
        cameraControl: false,
      }}
    >
      {areas.map((a) => {
        const center = { lat: a.center.latitude, lng: a.center.longitude };
        return (
          <Fragment key={a.id}>
            {a.radius !== null && (
              <CircleF
                center={center}
                radius={a.radius}
                options={{
                  clickable: false,
                  strokeColor: colors.area,
                  strokeOpacity: 0.8,
                  strokeWeight: 1.5,
                  fillColor: colors.area,
                  fillOpacity: 0.12,
                  zIndex: 0,
                }}
              />
            )}
            <MarkerF
              position={center}
              clickable={false}
              zIndex={1}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: colors.area,
                fillOpacity: 1,
                strokeColor: colors.ring,
                strokeWeight: 2,
              }}
            />
            {/* Non-interactive pane — the label never catches pin or background clicks. */}
            <OverlayViewF
              position={center}
              mapPaneName={OverlayView.OVERLAY_LAYER}
              getPixelPositionOffset={pinOffset}
            >
              <span className='attendance-live__area-label'>{a.name}</span>
            </OverlayViewF>
          </Fragment>
        );
      })}
      {users.map((u) => {
        const selected = u.companyUserId === selectedId;
        const status = statusOf(u);
        return (
          <OverlayViewF
            key={u.companyUserId}
            position={{ lat: u.lastLocation.latitude, lng: u.lastLocation.longitude }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={pinOffset}
          >
            <div
              className={[
                'attendance-live__pin-wrap',
                !u.online && 'attendance-live__pin-wrap--offline',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {selected && (
                // Clicks inside the card belong to the card — the map's
                // background click (which clears the selection) must not see them.
                <span className='attendance-live__pin-card' onClick={(e) => e.stopPropagation()}>
                  <span className='attendance-live__pin-head'>
                    <Avatar initials={initialsOf(u.fullName)} size={38} tone={toneOf(u.fullName)} />
                    <span className='attendance-live__pin-name'>{u.fullName}</span>
                    <IconButton
                      icon='x'
                      variant='ghost'
                      size='sm'
                      aria-label={t('modal_close')}
                      onClick={() => onSelect(null)}
                    />
                  </span>
                  <span className='attendance-live__pin-facts'>
                    <span className='attendance-live__pin-fact'>
                      <span className='attendance-live__pin-fact-label'>
                        {t('attendance_pin_status')}
                      </span>
                      <span
                        className={[
                          'attendance-live__pin-state',
                          status.modifier && `attendance-live__pin-state--${status.modifier}`,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {t(status.key)}
                      </span>
                    </span>
                    <span className='attendance-live__pin-fact'>
                      <span className='attendance-live__pin-fact-label'>
                        {t('attendance_pin_last_seen')}
                      </span>
                      <span className='attendance-live__pin-fact-value'>
                        {u.lastPingAt ? fmtLastSeen(u.lastPingAt, t) : '—'}
                      </span>
                    </span>
                  </span>
                  <span className='attendance-live__pin-actions'>
                    <Button onClick={comingSoon} size='sm'>
                      {t('attendance_live_pull_latest')}
                    </Button>
                    <span className='attendance-live__pin-hint'>
                      {t('attendance_pin_pull_hint')}
                    </span>
                    <Button
                      variant='secondary'
                      onClick={() => onShowRoute(u.companyUserId)}
                      size='sm'
                    >
                      {t('attendance_pin_route_history')}
                    </Button>
                  </span>
                </span>
              )}
              <button
                type='button'
                className={[
                  'attendance-live__pin',
                  u.online ? 'attendance-live__pin--online' : 'attendance-live__pin--offline',
                  selected && 'is-selected',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={u.fullName}
                aria-pressed={selected}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(selected ? null : u.companyUserId);
                }}
              >
                <Avatar initials={initialsOf(u.fullName)} size={30} tone={toneOf(u.fullName)} />
              </button>
              {!u.online && (
                <span className='attendance-live__pin-tag' aria-hidden='true'>
                  {t('attendance_live_offline')}
                </span>
              )}
            </div>
          </OverlayViewF>
        );
      })}
    </GoogleMap>
  );
};

export default LiveLocationsMap;
